/**
 * @version 1.0
 * @author Kelnik Studios {http://kelnik.ru}
 * @link https://kelnik.gitbooks.io/kelnik-documentation/content/front-end/components/пока нет documentation
 */

/**
 * DEPENDENCIES
 */
import 'url-search-params-polyfill';
import axios from 'axios';
import Cluster from './cluster';
import Controls from './controls';
import {DESKTOP_BREAKPOINT} from '../../common/scripts/constants';
import mapUtils from './utils';
import Marker from './marker';
import Observer from '../../common/scripts/observer';
import {Utils} from '../../common/scripts/utils';

const observer = new Observer();

class YandexMap {
    constructor(ymaps) {
        /**
         * Экземпляр загруженных яндекс карт.
         */
        this.ymaps = ymaps;

        /**
         * Экземпляр конструктора геообъектов
         */
        this.geoObjectCollection = new this.ymaps.GeoObjectCollection();

        /**
         * Все настройки карты
         * @type {Object}
         */
        this.settings = {};

        /**
         * Яндексовый объект карты
         * @type {null}
         */
        this.map = null;

        /**
         * URL по которому будет выполнен ajax запрос за данными
         */
        this.url = '';

        /**
         * Класса DOM-элемента в который будет вставлена карта
         */
        this.baseClass = '.j-yandex-map-base';

        /**
         * Класса DOM-элемента прелоадера
         */
        this.preloaderClass = '.j-yandex-map-preloader';

        /**
         * Брейкпоинт, после которого маркеры открываются не по клику, а по ховеру
         * @type {number}
         */
        this.desktopBreakpoint = DESKTOP_BREAKPOINT;

        /**
         * Брейкпоинт, ниже которого на touch-устройствах карта листается двумя пальцами
         */
        this.tabletBreakpoint = 1024;
    }

    /**
     * Метод принимает настройки из вне и запускает процесс инициализации карт
     * @param {object} outerOptions - опции из скрипта
     */
    async init(outerOptions) {
        this.outerOptions = outerOptions;
        this.mapWrapper = outerOptions.wrapper;
        this.content = this.mapWrapper.querySelector(this.baseClass);
        this.id = this.content.id;
        this.url = this.mapWrapper.dataset.ajax;
        this.json = this.mapWrapper.dataset.json;

        await this._runMap();
        this._subscribes();
    }

    /**
     * Метод содержит в себе колбэки на события других модулей
     */
    _subscribes() {
        this._showGeoObject = this._showGeoObject.bind(this);
        this._hideGeoObject = this._hideGeoObject.bind(this);

        observer.subscribe('addFilterItem', (markerType) => {
            this._toggleGeoObjects(markerType, this._showGeoObject);
        });

        observer.subscribe('removeFilterItem', (markerType) => {
            this._toggleGeoObjects(markerType, this._hideGeoObject);
        });

        observer.subscribe('map:showAllMarkers', () => {
            this.showAllMarkers();
        });

        observer.subscribe('map:hideAllMarkers', () => {
            this.hideAllMarkers();
        });
    }

    /**
     * Основной метод, который запускает выполнение всех остальных методов. При ошибке кладет весь остальной процесс.
     * @private
     */
    async _runMap() {
        await this._setSettings();
        await this._initYandexMap();
        this._setDeviceSettings();
        this._initMarker();
        this._initControls();
        this._initCluster();
        this._bindEvents();
        this._showMap();
        this._infrastructureToggle();

        if (this.settings.polygon && this.settings.polygon.length) {
            this._initPolygon();
        }
    }

    _bindEvents() {
        const events = ['resize', 'orientationchange'];

        events.forEach((event) => {
            window.addEventListener(event, Utils.debounce(this._resize.bind(this), 100));
        });
    }

    _resize() {
        this._setDeviceSettings();
    }

    /**
     * Метод собирает все настройки в единый объект. Если нет ответа от сервера, карта все равно будет работать
     * с дефолтными настройками
     * @private
     */
    async _setSettings() {
        this._connectSettings(this._setDefaultSettings());
        this._connectSettings(this._setScriptSettings());
        this._preBody();

        if (this.url) {
            try {
                const response = await axios.post(this.url, this.body);

                this._connectSettings(response.data.data);
            } catch (err) {
                console.error(`Не удалось получить данные от сервера, карта работает с дефолтными настройками ${err}`);
            }

            return;
        }

        if (this.json) {
            const decode = JSON.parse(decodeURIComponent(escape(window.atob(this.json))));

            this._connectSettings(decode.data);
        }
    }

    /**
     * Метод запускает создание экземпляра яндекс карт. Здесь происходит непосредственная вставка карты на страницу.
     * При ошибке выбросит исключение и положит весь остальной процесс. Т.к при ошибке инита нет смысла выполнять
     * дальнейшие методы
     * @return {Promise<void>} обещание на инициализацию карт
     * @private
     */
    async _initYandexMap() {
        const insertMap = () => {
            return new Promise((resolve) => {
                this.map = new this.ymaps.Map(this.id, this.settings, {
                    suppressMapOpenBlock: true,

                    /* перебить рекламные плагины */
                    fullscreenZIndex: 2147483647
                });

                if (this.map) {
                    resolve();
                } else {
                    throw new Error();
                }
            });
        };

        try {
            return await insertMap();
        } catch (err) {
            console.error(`При инициализации Яндекс Карт произошла критическая ошибка ${err}`);
            throw new Error();
        }
    }

    /**
     * Склеивает настройки из одного источника в общую настройку всей карты.
     * @param {object} outerSettings - настройки из вне.
     * @private
     */
    _connectSettings(outerSettings) {
        if (typeof outerSettings === `object`) {
            Object.assign(this.settings, outerSettings);
        }
    }

    /**
     * Устанавливает настройки по умолчанию.
     * @return {{
     * center: number[],
     * mapTypeControl: boolean,
     * zoom: number,
     * minZoom: number,
     * maxZoom: number,
     * zoomStep: number,
     * controls: Array,
     * zoomControl: boolean,
     * customZoomControl: boolean,
     * fullScreenControl: boolean,
     * customFullScreenControl: boolean,
     * height: number,
     * zoomScroll: boolean}} - Объект настройки карты
     * @private
     */
    _setDefaultSettings() {
        const latCenter = 59.939014;
        const lngCenter = 30.315545;
        const heightMap = this.content.offsetHeight;
        /* eslint-disable */
        return {
            center                 : [latCenter, lngCenter],
            mapTypeControl         : false,
            autoZoom               : false, // setBounds при загрузке
            zoom                   : 12,
            zoomMargin             : 250, //отступы при setBounds
            minZoom                : 10,
            maxZoom                : 18,
            zoomStep               : 1,
            controls               : [],
            zoomControl            : true,
            customZoomControl      : true,
            fullScreenControl      : true,
            customFullScreenControl: true,
            height                 : heightMap,
            zoomScroll             : false,
            markers                : []
        };
        /* eslint-enabled*/
    }

    /**
     * Возвращает настройки из скрипта - app.js
     * @return {object} - данные указанные в вызове метода init в app.js
     * @private
     */
    _setScriptSettings() {
        return this.outerOptions;
    }

    _setDeviceSettings() {
        if (!this.map) {
            return;
        }

        const isDraggable = this.map.behaviors.isEnabled('drag');
        if (Utils.isTouchDevice()
            && window.innerWidth < this.tabletBreakpoint) {
            if (isDraggable) {
                this.map.behaviors.disable('drag');
            }
        } else if (!isDraggable) {
            this.map.behaviors.enable('drag');
        }
    }

    /**
     * Инициализация маркера на визуальном выборщике.
     */
    _initMarker() {
        this.settings.markers.forEach((item) => {
            if (item.type === 'commerce') item.isNotClusterize = true;
            const marker = new Marker();
            this.geoObjectCollection.add(marker.createHtmlMarker(item));
            marker.closeBalloonOnClickMap(this.map);
        });

        this.geoObjectCollectionArray = this.geoObjectCollection.toArray().map(x => x);
        this.map.geoObjects.add(this.geoObjectCollection);

        if (this.geoObjectCollectionArray.length > 1 && this.settings.autoZoom) {
            this.map.setBounds(this.geoObjectCollection.getBounds(), {
                zoomMargin: this.settings.zoomMargin
            });
        }

        this._bindMapClick();
    }

    /**
     * Закрытие панели карты
     * @private
     */
    _bindMapClick() {
        const bindMapClick = new CustomEvent('bindMapClick');
        window.dispatchEvent(bindMapClick);

        this.map.events.add('click', () => {
            if (window.innerWidth < this.desktopBreakpoint) {
                window.dispatchEvent(bindMapClick);
            }
        });
    }

    /**
     * Метод устанавливает касмтомные контролы и прочие элементы управления.
     * @private
     */
    _initControls() {
        const controls = new Controls();

        controls.init(this.map, this.settings, this.ymaps);
    }

    /**
     * Метод инициализирует кластеры
     * @private
     */
    _initCluster() {
        if (this.outerOptions.cluster) {
            this.cluster = new Cluster();

            this.cluster.init(this.geoObjectCollection);
        }
    }

    /**
     * Метод скрывает прелоадер и показывает карту по завершению выполнения всех методов.
     * @private
     */
    _showMap() {
        if (this.preloader) {
            this.preloader = this.mapWrapper.querySelector(this.preloaderClass);
            this.preloader.style.opacity = '0';
        }

        this.content.style.opacity = '1';
    }


    /**
     * Подготавливает тело ajax-запроса, в данном случае это гет-параметры, если они есть.
     * @private
     */
    _preBody() {
        const get = window.location.search;

        if (!get) {
            this.body = '';

            return;
        }

        this.body = new FormData();

        const url = new URLSearchParams(get);
        const key = 0;
        const value = 1;

        for (const item of url.entries()) {
            this.body.append(item[key], item[value]);
        }
    }

    /**
     * Отображает геообъекты на карте и в кластерах
     * @param {String} geoObject - тип маркера
     * @private
     */
    _showGeoObject(geoObject) {
        this.cluster.addGeoObjectToClusterer(geoObject);
    }

    /**
     * Скрывает геообъекты на карте и в кластерах
     * @param {String} geoObject - объект карты
     * @private
     */
    _hideGeoObject(geoObject) {
        this.cluster.removeGeoObjectToClusterer(geoObject);
    }

    /**
     * Обёртка для переключения геообъектов по типу
     * @param {String} itemType - тип геообъекта
     * @param {Function} callback - колбэк, который будет применён к геообъекту
     * @private
     */
    _toggleGeoObjects(itemType, callback) {
        this.geoObjectCollectionArray.forEach((geoObject) => {
            if (geoObject.options.get('data').type === itemType) {
                callback(geoObject);
            }
        });
    }

    showAllMarkers() {
        this.geoObjectCollectionArray.forEach((geoObject) => {
            if (geoObject.options.get('data').type !== 'object') {
                this._showGeoObject(geoObject);
            }
        });
    }

    hideAllMarkers() {
        this.geoObjectCollectionArray.forEach((geoObject) => {
            if (geoObject.options.get('data').type !== 'object') {
                this._hideGeoObject(geoObject);
            }
        });
    }

    /**
     * Открывает баллун по клику на кнопку.
     * @param {string} id - дата атрибут из какого-либо объекта
     * @param {string} type - параметр в геообъекте по которому будет производиться поиск
     * @private
     */
    _openBalloon(id, type) {
        const idButton = Number(id);

        this.geoObjectCollection.each((item) => {
            const idMarker = Number(item.options.get(type));

            if (idButton === idMarker) {
                const panel = item.options.get('panel');
                const coords = item.geometry.getCoordinates();

                mapUtils.needBalloonPanel() ? panel.open(item) : item.balloon.open();

                // this._scrollToMap();
                this._setCenterMap(coords);
            }
        });
    }

    // /**
    //  * Скроллит к карте. Пока только на мобилке.
    //  */
    // _scrollToMap() {
    //     if (mapUtils.isMobile()) {
    //         scrollBy.init();
    //         scrollBy.scrollToElement(this.mapWrapper);
    //     }
    // }

    /**
     * Центрирует карту относительно координат
     * @param {array} coords - координаты центра.
     * @private
     */
    _setCenterMap(coords) {
        if (mapUtils.isMobile()) {
            this.map.setCenter(coords, this.settings.zoom);
        }
    }

    _initPolygon() {
        const polygon = new this.ymaps.Polygon([this.settings.polygon],{}, {
            // Убираем видимость обводки.
            fillColor: this.settings.polygonFill || '#0bb7494d',
            stroke: true,
            strokeColor: this.settings.polygonStroke || '#1BCB5B'
        });

        this.map.geoObjects.add(polygon);
    }

    _infrastructureToggle() {
        const toggle = this.mapWrapper.querySelector('.j-infrastructure-toggle');

        if (!toggle) {
            return;
        }

        const input = toggle.querySelector('input');

        this._infrastructureToggleChecked(input);

        toggle.addEventListener('click', () => {
            if (input.checked === false) {
                input.checked = true;
                this.geoObjectCollectionArray.forEach((geoObject) => {
                    if (geoObject.options.get('data').type === 'infrastructure') {
                        this._showGeoObject(geoObject);
                    }
                });
            } else {
                input.checked = false;
                this.geoObjectCollectionArray.forEach((geoObject) => {
                    if (geoObject.options.get('data').type === 'infrastructure') {
                        this._hideGeoObject(geoObject);
                    }
                });
            }
        });
    }

    _infrastructureToggleChecked(input) {
        if (input.checked === false) {
            console.log('_infrastructureToggleChecked: false', input)
            this.geoObjectCollectionArray.forEach((geoObject) => {
                if (geoObject.options.get('data').type === 'infrastructure') {
                    this._hideGeoObject(geoObject);
                }
            });
        } else {
            console.log('_infrastructureToggleChecked: true', input)
            this.geoObjectCollectionArray.forEach((geoObject) => {
                if (geoObject.options.get('data').type === 'infrastructure') {
                    this._showGeoObject(geoObject);
                }
            });
        }
    }

}

export default YandexMap;
